import PopUpNotification, {
    DeliveryTypes,
    ProviderTargets,
} from '@advanza/interfaces/PopUpNotification'
import { Input } from '@advanza/shadcn-ui/src/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@advanza/shadcn-ui/src/components/ui/table'
import { Button, Divider, Icon } from '@advanza/ui'
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    SortingState,
    Table as TableInstance,
    useReactTable,
} from '@tanstack/react-table'
import { ARCHIVED_COLUMN_ID } from 'components/popUpNotifications/PopUpNotificationColumnsDef'
import PopUpNotificationsDataTableRow from 'components/popUpNotifications/PopUpNotificationsDataTableRow'
import PopUpNotificationsFilterBar from 'components/popUpNotifications/PopUpNotificationsFilterBar'
import { useOfficeCountry } from 'hooks/popUpnotificationsHooks'
import { useState } from 'react'

interface DataTableProps {
    columns: ColumnDef<PopUpNotification>[]
    data: PopUpNotification[]
}
interface FilterInputProps {
    table: TableInstance<PopUpNotification>
    column: string
}
const FilterInput = ({ table, column }: FilterInputProps) => (
    <Input
        placeholder={`Filter by ${column}...`}
        value={(table.getColumn(column)?.getFilterValue() as string) ?? ''}
        onChange={(event) => table.getColumn(column)?.setFilterValue(event.target.value)}
        className="w-64"
    />
)

export function PopUpNotificationsDataTable({ columns, data }: DataTableProps) {
    const country = useOfficeCountry()
    const [sorting, setSorting] = useState<SortingState>([])
    const [expanded, setExpanded] = useState({})
    const [toggledRowId, setToggledRowId] = useState<number | false>(false)
    const [tableData, setTableData] = useState<PopUpNotification[]>(data)
    // Archived notifications are fetched along with the rest but hidden until the status filter
    // asks for them.
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
        { id: ARCHIVED_COLUMN_ID, value: false },
    ])

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnFiltersChange: setColumnFilters,
        onExpandedChange: setExpanded,
        state: {
            sorting,
            expanded,
            columnFilters,
        },
    })

    const addRow = () => {
        const newId = Date.now()

        const newRow: PopUpNotification = {
            id: newId,
            // @ts-ignore
            country_code: country,
            title: '',
            label: '',
            content: '',
            users_target: ProviderTargets.ALL,
            specific_targets: undefined,
            status: 0,
            delivery_type: DeliveryTypes.POP_UP,
            archived: false,
        }
        setTableData([newRow, ...tableData])
        setToggledRowId(newId)
    }

    const handleRowSave = (updatedRow: PopUpNotification) => {
        setTableData((prev) =>
            prev.map((row) =>
                row.pop_up_notification_id === updatedRow.pop_up_notification_id ||
                (!row.pop_up_notification_id && row.id === updatedRow.id)
                    ? { ...updatedRow }
                    : row,
            ),
        )
    }

    const handleRowToggle = (rowId: number) => {
        setToggledRowId((current) => (current === rowId ? false : rowId))
    }

    return (
        <div className="">
            <div className="flex flex-wrap items-center gap-1">
                <FilterInput column="title" table={table} />
                <PopUpNotificationsFilterBar table={table} />
                <Button name="text" onClick={addRow}>
                    <Icon name="add" /> add notification
                </Button>
            </div>
            <Divider m />
            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef.header,
                                                  header.getContext(),
                                              )}
                                    </TableHead>
                                )
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => {
                            const rowId =
                                row.original.pop_up_notification_id || row.original.id || Date.now()
                            return (
                                <PopUpNotificationsDataTableRow
                                    key={`row-${rowId}`}
                                    row={row}
                                    toggledRowId={toggledRowId}
                                    toggleRowSelection={() => handleRowToggle(rowId)}
                                    onSaveRow={handleRowSave}
                                />
                            )
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center">
                                No results.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export default PopUpNotificationsDataTable
